/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/features/chat/
// ChatRoomHeader.tsx`. The directory lookup that produced the
// subtitle on web is now hoisted into the host — this component is
// purely presentational and only needs `peerName`, `peerPresence`,
// and an optional `peerSubtitle` string the host computes upstream.
//
// Figma reference: chat-room frames at `3412:5136` (1 on 1 chat) and
// `3412:5207` (Group chat) — these don't draw the AppBar directly,
// but the surrounding "1426 wide / dark `#11131A` surface" places
// the header at 64h with 24px horizontal padding to match the
// composer at `2804:26702` (Chat / Composer / Mode / Typing /
// Default — w=1426 h=64 pad L24/R24/T8/B16). Title text is 17px
// (headline) with a 14px caption subtitle pulled from the typing /
// presence indicator block (`2918:10464` — typing-one-person —
// 17px label).
//
// Conversion notes:
//   - `<header>` -> `<View>` (RN has no equivalent semantic tag; the
//     accessibility role is implicit via `accessibilityRole="header"`).
//   - `<button>` -> `<Pressable>`.
//   - The original web file applied `transition-colors`; NativeWind
//     ignores CSS transitions, so the hover affordance is reduced to
//     the static `hover:` class which the web target resolves but
//     the RN target turns into a no-op press feedback.

import { BadgeCheck, Phone, Video } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { Avatar } from "../components/Avatar";
import { CallDropdownMenu } from "../chat/CallDropdownMenu";
import type { ChatPresence, ChatRoomHeaderProps } from "./ChatRoom.types";

// Map the chat presence values onto the shared `Avatar` component's
// `PresenceStatus` shape. The shared Avatar accepts "online" | "away"
// | "offline" | null, so the conversion is identity-shaped today but
// the indirection keeps the v2 surface independent of the underlying
// Avatar contract.
function presenceForAvatar(p: ChatPresence | undefined): ChatPresence {
	if (p === "online" || p === "away" || p === "offline") return p;
	return null;
}

export function ChatRoomHeader(props: ChatRoomHeaderProps): JSX.Element {
	const {
		peerName,
		peerAvatarUri,
		peerPresence,
		peerSubtitle,
		peerVerified,
		peerStatusEmoji,
		activeCall,
		onAudioCall,
		onVideoCall,
		onAudioCallMenu,
		onOpenChatSettings,
		callsDisabled,
	} = props;

	// Design-system chat-header (3171:93103) is a single clean row — no
	// default "Online" line. A subtitle only renders when the host
	// explicitly supplies one (typing indicator / "N members"); presence
	// is conveyed by the avatar dot.
	const subtitle = peerSubtitle ?? null;

	// Local expanded state for the call dropdown (Figma 2804:27521).
	// We only mount the popover when the host passes `onAudioCallMenu`;
	// otherwise the plain `onAudioCall` press handler is used.
	const [callMenuOpen, setCallMenuOpen] = useState(false);

	return (
		<View
			accessibilityRole="header"
			// Figma 2804:25678 (Chat Title - 1): h=56, paddingT/B=4,
			// paddingL/R=16, inner itemSpacing=8. Background `#0A0B0F`
			// dark surface vs `#FFFFFF` light-surface-primary token.
			// Bottom hairline border uses `rgba(255,255,255,0.04)` which
			// Figma applies on the composer's inner search field as a
			// separator.
			// Header bg matches the chat-room body (`#11131A`) so the
			// surface reads as one continuous canvas — Figma 2804:25678
			// shows the title+composer on the same fill. Previously
			// `#0A0B0F` made the header a visibly darker stripe.
			className="
				flex flex-row items-center gap-sm
				h-14 px-lg py-xs shrink-0
				border-b border-light-border-secondary dark:border-white/[0.04]
				bg-light-surface-primary dark:bg-[#11131A]
			"
		>
			{/* Avatar + name open the Chat Settings panel (1:1 / group info). */}
			<Pressable
				accessibilityRole={onOpenChatSettings !== undefined ? "button" : undefined}
				accessibilityLabel={
					onOpenChatSettings !== undefined ? `Open ${peerName} settings` : undefined
				}
				onPress={onOpenChatSettings}
				className="flex-1 min-w-0 flex flex-row items-center gap-sm active:opacity-80"
			>
				<Avatar
					initials={peerName.charAt(0).toUpperCase() || "?"}
					{...(peerAvatarUri !== undefined ? { imageUri: peerAvatarUri } : {})}
					size="small"
					presence={presenceForAvatar(peerPresence)}
				/>
				<View className="flex-1 min-w-0 flex flex-col">
					{/*
						Name row — Design-system 3171:93103: name (16/bold) +
						optional verified blue badge + optional status emoji,
						all on one line.
					*/}
					<View className="flex flex-row items-center gap-1.5 min-w-0">
						<Text
							numberOfLines={1}
							className="font-sans text-[16px] font-bold text-light-text-primary dark:text-[#F5F6F8] leading-[22px] shrink"
						>
							{peerName}
						</Text>
						{peerVerified === true ? (
							<BadgeCheck size={16} strokeWidth={2} color="#1796C4" />
						) : null}
						{peerStatusEmoji !== undefined && peerStatusEmoji.length > 0 ? (
							<Text className="text-[14px] leading-[18px]">{peerStatusEmoji}</Text>
						) : null}
					</View>
					{subtitle !== null ? (
						<Text
							numberOfLines={1}
							// Subtitle (typing / "N members"): Lato 400 14/19.6 #B5BAC3.
							className="font-sans text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]"
						>
							{subtitle}
						</Text>
					) : null}
				</View>
			</Pressable>

			{/* Right cluster — Figma chat-header 4523:10124. Default: Phone + Video
			    (36×36 round). call_active: a green "Join call"/"Return" pill (replaces
			    the Phone button) + the Video button stays. */}
			<View
				className="flex flex-row items-center gap-xs"
				pointerEvents={callsDisabled === true ? "none" : "auto"}
				style={callsDisabled === true ? { opacity: 0.4 } : undefined}
			>
				{activeCall !== undefined ? (
					// Figma `state=call_active`: pill 32h, r8, fill #41921C, pad 8/4,
					// "Join call" 14/700 #E8EAED + "+N" 12/700 #FFFFFF.
					<Pressable
						accessibilityRole="button"
						accessibilityLabel={activeCall.joined ? "Return to call" : "Join call"}
						onPress={activeCall.onPress ?? (() => undefined)}
						className="h-8 flex-row items-center gap-1.5 rounded-[8px] bg-[#41921C] px-2 active:opacity-80"
					>
						<Text className="font-sans text-[14px] font-bold leading-[20px] text-[#E8EAED]">
							{activeCall.joined ? "Return" : "Join call"}
						</Text>
						{activeCall.participantCount !== undefined && activeCall.participantCount > 0 ? (
							<Text className="font-sans text-[12px] font-bold leading-[17px] text-white">
								{`+${activeCall.participantCount}`}
							</Text>
						) : null}
					</Pressable>
				) : onAudioCallMenu !== undefined ? (
					<View className="relative">
						<CallDropdownMenu
							isExpanded={callMenuOpen}
							onChangeExpanded={setCallMenuOpen}
							onSelect={onAudioCallMenu}
						/>
					</View>
				) : (
					<HeaderIconButton
						icon={Phone}
						label="Audio call"
						onPress={onAudioCall}
					/>
				)}
				<HeaderIconButton
					icon={Video}
					label="Video call"
					onPress={onVideoCall}
				/>
			</View>
		</View>
	);
}

interface HeaderIconButtonProps {
	readonly icon: LucideIcon;
	readonly label: string;
	readonly onPress?: (() => void) | undefined;
}

function HeaderIconButton({
	icon: Icon,
	label,
	onPress,
}: HeaderIconButtonProps): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={label}
			onPress={onPress ?? (() => undefined)}
			disabled={onPress === undefined}
			// 38x38 rounded chrome matches the Figma `iconsax-*`
			// container in `2804:26711` (Frame 1000004369 — 38x38
			// r=10000) used by the composer's mic / +/ send buttons.
			// `transition-colors` dropped (no NativeWind driver).
			className="
				w-[38px] h-[38px] rounded-full flex items-center justify-center
				text-light-text-primary hover:text-light-text-primary hover:bg-light-surface-tertiary
				dark:text-white dark:hover:text-white dark:hover:bg-white/[0.06]
			"
		>
			{/*
				lucide-react-native icons don't inherit from CSS `text-*`
				classes; pass `color` explicitly. Figma chat-header
				(2804:25343) draws the call/video icons WHITE (#FFFFFF /
				measured ~#E8EAED) in the default resting state — not the
				`#888d96` composer-mic neutral — so the resting stroke is
				white here and the hover bg supplies the press affordance.
			*/}
			<Icon size={24} strokeWidth={1.75} color="#FFFFFF" />
		</Pressable>
	);
}
